import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Package, Search, Filter, Save, AlertCircle, CheckCircle, QrCode,
  Download, ScanLine, Eye, RotateCcw, ArrowUpDown, History, Camera, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [onlyLowStock, setOnlyLowStock] = useState(false);

  // Editing state: { [productId]: newStockValue }
  const [edits, setEdits] = useState({});
  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [savingBulk, setSavingBulk] = useState(false);

  // Scanner
  const [scannerMode, setScannerMode] = useState(false);
  const [scanInput, setScanInput] = useState('');
  const [scannedProduct, setScannedProduct] = useState(null);
  const [scanStock, setScanStock] = useState('');
  const scanInputRef = useRef(null);

  // Camera scanner
  const [cameraActive, setCameraActive] = useState(false);
  const cameraScannerRef = useRef(null);
  const html5QrRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup camera on unmount
      if (html5QrRef.current?.isScanning) {
        html5QrRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const startCamera = async () => {
    setCameraActive(true);
    // Wait for DOM element to appear
    setTimeout(async () => {
      try {
        const Html5QrcodeClass = (await import('html5-qrcode')).Html5Qrcode;
        const scanner = new Html5QrcodeClass('camera-scanner-region');
        html5QrRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decodedText) => {
            // Successful scan
            setScanInput(decodedText);
            await stopCamera();
            // Auto-lookup
            try {
              const res = await axiosInstance.get(`/inventory/lookup/${encodeURIComponent(decodedText)}`);
              setScannedProduct(res.data);
              setScanStock(String(res.data.current_stock));
              toast.success(`Gefunden: ${res.data.name}`);
            } catch (e) {
              toast.error(`Code "${decodedText}" nicht gefunden`);
            }
          },
          () => { /* scan failure - silent */ }
        );
      } catch (e) {
        toast.error('Kamera konnte nicht gestartet werden. Bitte Berechtigung erteilen.');
        setCameraActive(false);
      }
    }, 200);
  };

  const stopCamera = async () => {
    try {
      if (html5QrRef.current?.isScanning) {
        await html5QrRef.current.stop();
      }
    } catch (e) { /* ignore */ }
    setCameraActive(false);
  };

  // Audit
  const [showAudit, setShowAudit] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);

  // QR Preview Modal
  const [qrPreview, setQrPreview] = useState(null);
  const [qrPreviewUrl, setQrPreviewUrl] = useState('');

  useEffect(() => {
    if (!qrPreview) { setQrPreviewUrl(''); return; }
    let active = true;
    let blobUrl = '';
    axiosInstance.get(`/inventory/qrcode/${encodeURIComponent(qrPreview.sku)}`, { responseType: 'blob' })
      .then((res) => {
        if (!active) return;
        blobUrl = window.URL.createObjectURL(res.data);
        setQrPreviewUrl(blobUrl);
      })
      .catch(() => toast.error('QR-Code konnte nicht geladen werden'));
    return () => {
      active = false;
      if (blobUrl) window.URL.revokeObjectURL(blobUrl);
    };
  }, [qrPreview]);

  useEffect(() => { fetchInventory(); fetchCategories(); }, [search, categoryFilter, sortBy, onlyLowStock]);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (categoryFilter) params.set('category', categoryFilter);
      if (sortBy) params.set('sort_by', sortBy);
      if (onlyLowStock) params.set('only_low_stock', 'true');
      const res = await axiosInstance.get(`/inventory/products?${params}`);
      setItems(res.data.items || []);
    } catch (e) {
      toast.error('Fehler beim Laden des Inventars');
    } finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const res = await axiosInstance.get('/inventory/categories');
      setCategories(res.data.categories || []);
    } catch (e) { /* ignore */ }
  };

  const handleEdit = (id, value) => {
    const v = value === '' ? '' : parseInt(value, 10);
    if (value === '' || isNaN(v)) {
      const ne = { ...edits }; delete ne[id]; setEdits(ne);
    } else {
      setEdits({ ...edits, [id]: Math.max(0, v) });
    }
  };

  const resetEdits = () => { setEdits({}); setPreviewMode(false); setPreviewData([]); };

  const changedItems = useMemo(() => {
    return items.filter((i) => edits[i.id] !== undefined && edits[i.id] !== i.current_stock);
  }, [items, edits]);

  const handlePreview = async () => {
    if (changedItems.length === 0) {
      toast.error('Keine Änderungen vorhanden');
      return;
    }
    try {
      const changes = changedItems.map((i) => ({ product_id: i.id, new_stock: edits[i.id] }));
      const res = await axiosInstance.post('/inventory/preview', { changes, reason: 'Inventur' });
      setPreviewData(res.data.preview || []);
      setPreviewMode(true);
    } catch (e) {
      toast.error('Vorschau fehlgeschlagen');
    }
  };

  const handleConfirmSave = async () => {
    setSavingBulk(true);
    try {
      const changes = changedItems.map((i) => ({ product_id: i.id, new_stock: edits[i.id] }));
      const res = await axiosInstance.post('/inventory/bulk-update', { changes, reason: 'Inventur' });
      toast.success(`${res.data.updated} Artikel aktualisiert`);
      setEdits({});
      setPreviewMode(false);
      setPreviewData([]);
      fetchInventory();
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally { setSavingBulk(false); }
  };

  // Normalize short numeric inputs like "25" → "SH00025" so the user can type
  // just the article number without the leading prefix and zero-padding.
  // The auto-prefix logic also tries "SH00025" if the literal lookup fails.
  const normalizeSku = (raw) => {
    const trimmed = (raw || '').trim().toUpperCase();
    if (!trimmed) return [trimmed];
    // Pure number: try common pad widths (4 = SH0025, 5 = SH00025)
    if (/^\d{1,5}$/.test(trimmed)) {
      const candidates = [];
      for (const width of [4, 5, 3]) {
        const padded = 'SH' + trimmed.padStart(width, '0');
        if (!candidates.includes(padded)) candidates.push(padded);
      }
      return candidates;
    }
    return [trimmed];
  };

  const handleScanLookup = async () => {
    const raw = scanInput.trim();
    if (!raw) return;
    const candidates = normalizeSku(raw);
    if (!candidates.includes(raw)) candidates.push(raw);
    if (!candidates.includes(raw.toUpperCase())) candidates.push(raw.toUpperCase());

    for (const sku of candidates) {
      try {
        const res = await axiosInstance.get(`/inventory/lookup/${encodeURIComponent(sku)}`);
        setScannedProduct(res.data);
        setScanStock(String(res.data.current_stock));
        if (sku !== raw) toast.success(`Gefunden als ${res.data.sku || sku}`);
        return;
      } catch {
        // try next
      }
    }
    toast.error(`Code "${raw}" nicht gefunden`);
    setScannedProduct(null);
  };

  const handleScanSave = async () => {
    if (!scannedProduct) return;
    const newStock = parseInt(scanStock, 10);
    if (isNaN(newStock) || newStock < 0) { toast.error('Ungültiger Bestand'); return; }
    try {
      await axiosInstance.post('/inventory/scan-update', {
        sku: scannedProduct.sku,
        new_stock: newStock,
      });
      toast.success(`${scannedProduct.name}: ${scannedProduct.current_stock} → ${newStock}`);
      setScannedProduct(null);
      setScanInput('');
      setScanStock('');
      scanInputRef.current?.focus();
      fetchInventory();
    } catch (e) {
      toast.error('Aktualisierung fehlgeschlagen');
    }
  };

  const handleDownloadAllQR = async () => {
    try {
      const response = await axiosInstance.get('/inventory/qrcode-pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url; link.download = 'qrcodes_homeyoursoul.pdf';
      link.click(); window.URL.revokeObjectURL(url);
      toast.success('QR-Codes als PDF heruntergeladen');
    } catch (e) {
      toast.error('PDF-Generierung fehlgeschlagen');
    }
  };

  const handleDownloadLabels = async () => {
    try {
      const response = await axiosInstance.get('/inventory/labels-pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url; link.download = 'etiketten_homeyoursoul.pdf';
      link.click(); window.URL.revokeObjectURL(url);
      toast.success('Etiketten als PDF heruntergeladen');
    } catch (e) {
      toast.error('PDF-Generierung fehlgeschlagen');
    }
  };

  const handleSingleQR = (item) => {
    setQrPreview(item);
  };

  const loadAudit = async () => {
    try {
      const res = await axiosInstance.get('/inventory/audit-log?limit=100');
      setAuditEntries(res.data.entries || []);
      setShowAudit(true);
    } catch (e) {
      toast.error('Verlauf konnte nicht geladen werden');
    }
  };

  if (previewMode) {
    return (
      <div className="min-h-screen py-4 px-3 sm:px-6" style={{ backgroundColor: '#F5EDE4' }} data-testid="inventory-preview">
        <div className="max-w-5xl mx-auto">
          <AdminPageHeader
            title="Änderungen prüfen & speichern"
            subtitle={`${previewData.length} Artikel ändern sich`}
            icon={Eye}
          />
          <Card className="rounded-none border-2 border-amber-400">
            <CardHeader className="bg-amber-50 py-3">
              <CardTitle className="text-base flex items-center gap-2 text-amber-900">
                <AlertCircle className="h-5 w-5" /> Bitte alle Änderungen prüfen
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAF7F2]">
                    <tr>
                      <th className="text-left p-3">SKU</th>
                      <th className="text-left p-3">Artikel</th>
                      <th className="text-right p-3">Alt</th>
                      <th className="text-right p-3">Neu</th>
                      <th className="text-right p-3">Differenz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((p, i) => (
                      <tr key={p.product_id} className={i % 2 ? 'bg-[#FAF7F2]' : 'bg-white'} data-testid={`preview-row-${p.sku}`}>
                        <td className="p-3 font-mono text-xs">{p.sku || '-'}</td>
                        <td className="p-3">{p.name}</td>
                        <td className="p-3 text-right text-gray-500">{p.old_stock}</td>
                        <td className="p-3 text-right font-semibold">{p.new_stock}</td>
                        <td className={`p-3 text-right font-bold ${p.difference > 0 ? 'text-green-700' : p.difference < 0 ? 'text-red-700' : 'text-gray-500'}`}>
                          {p.difference > 0 ? '+' : ''}{p.difference}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="mt-4 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setPreviewMode(false)} className="rounded-none" data-testid="cancel-preview-btn">
              <RotateCcw className="h-4 w-4 mr-2" /> Zurück zur Bearbeitung
            </Button>
            <Button onClick={handleConfirmSave} disabled={savingBulk} className="rounded-none bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-save-btn">
              <Save className="h-4 w-4 mr-2" /> {savingBulk ? 'Speichere...' : 'Ja, jetzt speichern'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 px-3 sm:px-6" style={{ backgroundColor: '#F5EDE4' }} data-testid="inventory-page">
      <div className="max-w-7xl mx-auto">
        <AdminPageHeader
          title="Inventar & Lagerbestand"
          subtitle="Bestände erfassen, Übersicht behalten, QR-Codes drucken"
          icon={Package}
        />

        {/* Action bar */}
        <div className="flex flex-wrap gap-2 mb-4">
          <Button
            data-testid="toggle-scanner-btn"
            onClick={() => setScannerMode(!scannerMode)}
            variant={scannerMode ? 'default' : 'outline'}
            className="rounded-none"
          >
            <ScanLine className="h-4 w-4 mr-2" /> Scanner {scannerMode ? 'aus' : 'an'}
          </Button>
          <Button
            data-testid="qr-pdf-btn"
            onClick={handleDownloadAllQR}
            variant="outline"
            className="rounded-none"
          >
            <Download className="h-4 w-4 mr-2" /> Alle QR-Codes (PDF)
          </Button>
          <Button
            data-testid="labels-pdf-btn"
            onClick={handleDownloadLabels}
            variant="outline"
            className="rounded-none"
          >
            <Download className="h-4 w-4 mr-2" /> Etiketten (PDF)
          </Button>
          <Button
            data-testid="audit-log-btn"
            onClick={loadAudit}
            variant="outline"
            className="rounded-none"
          >
            <History className="h-4 w-4 mr-2" /> Änderungs-Verlauf
          </Button>
        </div>

        {/* Scanner Mode */}
        {scannerMode && (
          <Card className="rounded-none border-2 border-blue-400 mb-4">
            <CardHeader className="bg-blue-50 py-3">
              <CardTitle className="text-base flex items-center gap-2 text-blue-900">
                <ScanLine className="h-5 w-5" /> Scanner-Modus
              </CardTitle>
              <p className="text-xs text-blue-800 mt-1">📷 Kamera nutzen, QR-Code scannen oder Artikelcode eingeben → Bestand aktualisieren</p>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="flex gap-2">
                <Input
                  ref={scanInputRef}
                  data-testid="scan-input"
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleScanLookup()}
                  placeholder="Nur Nummer (z.B. 25) oder vollständig (SH00025)"
                  className="font-mono"
                  autoFocus
                />
                <Button onClick={handleScanLookup} className="rounded-none bg-blue-600 text-white" data-testid="scan-lookup-btn">
                  Suchen
                </Button>
                <Button onClick={startCamera} className="rounded-none bg-green-600 hover:bg-green-700 text-white" data-testid="camera-scan-btn">
                  <Camera className="h-4 w-4 mr-2" /> Kamera
                </Button>
              </div>

              {/* Camera Overlay */}
              {cameraActive && (
                <div className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="camera-overlay">
                  <div className="bg-black/80 text-white p-3 flex justify-between items-center">
                    <div>
                      <p className="font-semibold">📷 QR-Code scannen</p>
                      <p className="text-xs text-white/70">Halten Sie den Code in den Rahmen</p>
                    </div>
                    <Button variant="ghost" onClick={stopCamera} className="text-white hover:bg-white/20" data-testid="camera-close-btn">
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                  <div className="flex-1 flex items-center justify-center p-4">
                    <div id="camera-scanner-region" ref={cameraScannerRef} className="w-full max-w-md bg-black" />
                  </div>
                  <div className="bg-black/80 text-white p-3 text-center text-sm">
                    Scan wird automatisch erkannt
                  </div>
                </div>
              )}

              {scannedProduct && (
                <div className="border-2 border-blue-300 bg-blue-50 p-4 space-y-3" data-testid="scan-found-product">
                  <div className="flex gap-3">
                    {scannedProduct.images?.[0] && (
                      <img src={scannedProduct.images[0]} alt="" className="w-16 h-16 object-cover" />
                    )}
                    <div className="flex-1">
                      <p className="font-mono text-xs text-blue-700">{scannedProduct.sku}</p>
                      <p className="font-semibold">{scannedProduct.name}</p>
                      <p className="text-xs text-gray-600">{scannedProduct.category}</p>
                      <p className="text-sm mt-1">Aktueller Bestand: <strong>{scannedProduct.current_stock}</strong></p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="0"
                      value={scanStock}
                      onChange={(e) => setScanStock(e.target.value)}
                      placeholder="Neuer Bestand"
                      className="text-lg font-bold text-center"
                      data-testid="scan-new-stock"
                      autoFocus
                    />
                    <Button onClick={handleScanSave} className="rounded-none bg-green-600 hover:bg-green-700 text-white" data-testid="scan-save-btn">
                      <Save className="h-4 w-4 mr-2" /> Speichern
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="rounded-none border-[#C4A084] mb-4">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  data-testid="search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="SKU, Name, Kategorie..."
                  className="pl-9"
                />
              </div>
              <select
                data-testid="category-filter"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Alle Kategorien</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                data-testid="sort-by"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="name">Name (A-Z)</option>
                <option value="sku">SKU</option>
                <option value="category">Kategorie</option>
                <option value="stock">Bestand (niedrig zuerst)</option>
              </select>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={onlyLowStock}
                  onChange={(e) => setOnlyLowStock(e.target.checked)}
                  data-testid="low-stock-filter"
                />
                Nur niedriger Bestand (≤5)
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Inventory Table - Desktop */}
        <Card className="rounded-none border-[#C4A084]">
          <CardContent className="p-0">
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F2] sticky top-0">
                  <tr>
                    <th className="text-left p-3 w-20">Bild</th>
                    <th className="text-left p-3 w-28">SKU</th>
                    <th className="text-left p-3">Artikel</th>
                    <th className="text-left p-3">Kategorie</th>
                    <th className="text-right p-3 w-24">Bestand</th>
                    <th className="text-center p-3 w-36" style={{ backgroundColor: '#FFF8E0' }}>
                      <span className="font-bold text-[#8B7355]">Neu erfassen ▶</span>
                    </th>
                    <th className="text-center p-3 w-20">QR</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan="7" className="p-6 text-center text-gray-500">Lädt...</td></tr>}
                  {!loading && items.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-gray-500">Keine Artikel gefunden</td></tr>}
                  {items.map((item, idx) => {
                    const edited = edits[item.id];
                    const isChanged = edited !== undefined && edited !== item.current_stock;
                    return (
                      <tr key={item.id} className={`${idx % 2 ? 'bg-[#FAF7F2]' : 'bg-white'} ${isChanged ? 'ring-2 ring-amber-400' : ''}`} data-testid={`inv-row-${item.sku || item.id}`}>
                        <td className="p-2">
                          {item.images?.[0] ? <img src={item.images[0]} alt="" className="w-12 h-12 object-cover" /> : <div className="w-12 h-12 bg-gray-100" />}
                        </td>
                        <td className="p-2 font-mono text-xs">{item.sku || '-'}</td>
                        <td className="p-2">
                          <p className="font-medium">{item.name}</p>
                          <p className="text-xs text-gray-500">{formatEUR(item.price)}</p>
                        </td>
                        <td className="p-2 text-xs">{item.category}</td>
                        <td className={`p-2 text-right font-semibold ${item.current_stock <= 5 ? 'text-red-600' : item.current_stock <= 10 ? 'text-amber-600' : 'text-gray-700'}`}>
                          {item.current_stock}
                        </td>
                        <td className="p-2 text-center" style={{ backgroundColor: isChanged ? '#FFF3CD' : '#FFFBEE' }}>
                          <Input
                            type="number"
                            min="0"
                            value={edits[item.id] !== undefined ? edits[item.id] : ''}
                            onChange={(e) => handleEdit(item.id, e.target.value)}
                            placeholder={`${item.current_stock}`}
                            className="text-center font-bold w-20 mx-auto h-9"
                            data-testid={`new-stock-input-${item.id}`}
                          />
                        </td>
                        <td className="p-2 text-center">
                          {item.sku && (
                            <Button size="sm" variant="ghost" onClick={() => handleSingleQR(item)} data-testid={`qr-btn-${item.sku}`}>
                              <QrCode className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className="sm:hidden divide-y">
              {loading && <p className="p-6 text-center text-gray-500">Lädt...</p>}
              {!loading && items.map((item) => {
                const edited = edits[item.id];
                const isChanged = edited !== undefined && edited !== item.current_stock;
                return (
                  <div key={item.id} className={`p-3 ${isChanged ? 'bg-amber-50' : 'bg-white'}`} data-testid={`inv-mobile-${item.sku || item.id}`}>
                    <div className="flex gap-3">
                      {item.images?.[0] ? <img src={item.images[0]} alt="" className="w-16 h-16 object-cover flex-shrink-0" /> : <div className="w-16 h-16 bg-gray-100 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-xs text-gray-500">{item.sku}</p>
                        <p className="font-medium text-sm truncate">{item.name}</p>
                        <p className="text-xs text-gray-500">{item.category}</p>
                        <p className="text-xs mt-1">Aktuell: <strong>{item.current_stock}</strong> Stk</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2 items-center">
                      <label className="text-xs text-[#8B7355] font-bold">Neu ▶</label>
                      <Input
                        type="number"
                        min="0"
                        value={edits[item.id] !== undefined ? edits[item.id] : ''}
                        onChange={(e) => handleEdit(item.id, e.target.value)}
                        placeholder={`${item.current_stock}`}
                        className="text-center font-bold flex-1 h-10"
                        style={{ backgroundColor: '#FFFBEE' }}
                        data-testid={`mobile-stock-${item.id}`}
                      />
                      {item.sku && (
                        <Button size="sm" variant="ghost" onClick={() => handleSingleQR(item)}>
                          <QrCode className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Sticky bottom save bar */}
        {changedItems.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-amber-400 shadow-lg p-3 z-40" data-testid="changes-bar">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div>
                <p className="font-semibold text-amber-900">{changedItems.length} Änderung(en) bereit</p>
                <p className="text-xs text-gray-600">Bitte vor dem Speichern prüfen</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetEdits} className="rounded-none" data-testid="reset-edits-btn">
                  Zurücksetzen
                </Button>
                <Button onClick={handlePreview} className="rounded-none bg-amber-500 hover:bg-amber-600 text-white" data-testid="preview-changes-btn">
                  <Eye className="h-4 w-4 mr-2" /> Vorschau & Speichern
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Audit modal */}
        {showAudit && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="audit-modal">
            <div className="bg-white max-w-4xl w-full max-h-[80vh] overflow-y-auto border-2 border-[#C4A084]">
              <div className="sticky top-0 bg-[#C4A084] text-white p-4 flex justify-between items-center">
                <h3 className="font-semibold">Änderungs-Verlauf (letzte 100)</h3>
                <Button variant="ghost" onClick={() => setShowAudit(false)} className="text-white hover:bg-white/20">Schließen</Button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F2]">
                  <tr>
                    <th className="p-2 text-left">Zeit</th>
                    <th className="p-2 text-left">SKU</th>
                    <th className="p-2 text-left">Artikel</th>
                    <th className="p-2 text-right">Alt → Neu</th>
                    <th className="p-2 text-left">Grund</th>
                  </tr>
                </thead>
                <tbody>
                  {auditEntries.map((e) => (
                    <tr key={e.id} className="border-b">
                      <td className="p-2 text-xs">{new Date(e.timestamp).toLocaleString('de-DE')}</td>
                      <td className="p-2 font-mono text-xs">{e.sku}</td>
                      <td className="p-2 text-xs">{e.name}</td>
                      <td className="p-2 text-right">
                        {e.old_stock} → <strong>{e.new_stock}</strong>
                        <span className={`ml-1 text-xs ${e.difference > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ({e.difference > 0 ? '+' : ''}{e.difference})
                        </span>
                      </td>
                      <td className="p-2 text-xs">{e.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* QR Preview Modal */}
        {qrPreview && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="qr-preview-modal" onClick={() => setQrPreview(null)}>
            <div className="bg-white max-w-md w-full p-6 border-2 border-[#C4A084]" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-4">
                <h3 className="font-semibold text-[#6D5A47]">QR-Code Prüfung</h3>
                <Button variant="ghost" size="sm" onClick={() => setQrPreview(null)} className="h-7 w-7 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex flex-col items-center gap-3 mb-4">
                <div className="bg-white p-3 border border-gray-200">
                  {qrPreviewUrl ? (
                    <img src={qrPreviewUrl} alt={`QR ${qrPreview.sku}`} className="w-56 h-56" data-testid="qr-preview-image" />
                  ) : (
                    <div className="w-56 h-56 bg-gray-100 flex items-center justify-center text-sm text-gray-400">Lädt...</div>
                  )}
                </div>
                <p className="font-mono text-lg font-bold tracking-widest" data-testid="qr-preview-sku">{qrPreview.sku}</p>
              </div>

              <div className="bg-[#FAF7F2] p-3 border border-[#E8E0D5] flex gap-3 items-center">
                {qrPreview.images?.[0] && (
                  <img src={qrPreview.images[0]} alt="" className="w-16 h-16 object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm" data-testid="qr-preview-name">{qrPreview.name}</p>
                  <p className="text-xs text-gray-500">{qrPreview.category}</p>
                  <p className="text-xs mt-1">Bestand: <strong>{qrPreview.current_stock}</strong> Stk</p>
                </div>
              </div>

              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 text-xs text-blue-900">
                <p className="font-semibold mb-1">So prüfen Sie die Zuordnung:</p>
                <p>1. Halten Sie den QR-Code mit Ihrem Handy in die Kamera-App</p>
                <p>2. Es sollte erscheinen: <strong className="font-mono">{qrPreview.sku}</strong></p>
                <p>3. Das ist die Artikelnummer von "<strong>{qrPreview.name}</strong>"</p>
              </div>

              <Button
                onClick={() => {
                  if (qrPreviewUrl) {
                    const link = document.createElement('a');
                    link.href = qrPreviewUrl;
                    link.download = `qr_${qrPreview.sku}.png`;
                    link.click();
                  }
                }}
                disabled={!qrPreviewUrl}
                className="w-full mt-4 rounded-none bg-[#8B7355] hover:bg-[#6D5A47] text-white"
                data-testid="qr-preview-download-btn"
              >
                <Download className="h-4 w-4 mr-2" /> QR-Code herunterladen
              </Button>
            </div>
          </div>
        )}

        {/* Spacer so sticky bar doesn't cover content */}
        {changedItems.length > 0 && <div className="h-20" />}
      </div>
    </div>
  );
}
